import { useEffect, useState, useMemo, useCallback } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import {
  Funnel, FileXls, FilePdf, MagnifyingGlass, X, Phone, ChatCircleDots, CircleNotch, NotePencil, Check, Tag, MessengerLogo, WhatsappLogo, MapPin,
} from '@phosphor-icons/react';

const fmtDate = (iso) => iso ? iso.slice(0, 10) : '—';
const ESTADOS_LEAD = ['Nuevo', 'Contactado', 'Interesado', 'Cotizando', 'Convertido a cliente', 'Perdido'];

const ClientesRedes = () => {
  const [filters, setFilters] = useState({ q: '', empresa: '', channel: '', departamento: '' });
  const [clients, setClients] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState({ xls: false, pdf: false });
  const [editing, setEditing] = useState(null);
  const [convirtiendo, setConvirtiendo] = useState(null);

  const cambiarEstado = async (c, estado) => {
    try {
      await api.patch(`/clientes/lead-estado/${encodeURIComponent(c.id)}`, { estado });
      setClients((prev) => prev.map((x) => x.id === c.id ? { ...x, lead_estado: estado } : x));
      toast.success(`Lead: ${estado}`);
    } catch { toast.error('No se pudo cambiar el estado'); }
  };

  const params = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  }, [filters]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clientes/redes-sociales?${params.toString()}`);
      setClients(r.data.clients || []);
      if (r.data.departamentos) setDepartamentos(r.data.departamentos);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando clientes');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const exportFile = async (kind) => {
    setExporting(prev => ({ ...prev, [kind]: true }));
    try {
      const url = kind === 'pdf' ? '/clientes/redes-sociales/export/pdf' : '/clientes/redes-sociales/export/excel';
      const mime = kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const r = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: mime });
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `clientes-redes-${Date.now()}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(u);
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch { toast.error('Error exportando'); }
    finally { setExporting(prev => ({ ...prev, [kind]: false })); }
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 min-h-screen bg-[var(--bg)]">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-[var(--text)] flex items-center gap-2">
            <ChatCircleDots size={22} weight="bold" className="text-[#10B981]" /> Clientes Redes Sociales
          </h1>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">Contactos del Inbox (WhatsApp / Messenger). Para campañas de marketing segmentadas.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportFile('pdf')} disabled={exporting.pdf || loading}
            className="px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="export-pdf">
            {exporting.pdf ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} weight="bold" />} PDF
          </button>
          <button onClick={() => exportFile('xls')} disabled={exporting.xls || loading}
            className="px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="export-clientes-redes">
            {exporting.xls ? <CircleNotch size={14} className="animate-spin" /> : <FileXls size={14} weight="bold" />} Excel
          </button>
        </div>
      </header>

      <section className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--text-muted)] flex items-center gap-1.5"><Funnel size={14} />Filtros</h3>
          <button onClick={() => setFilters({ q: '', empresa: '', channel: '', departamento: '' })} className="text-[10px] uppercase tracking-wider font-bold text-[#DC2626] hover:underline">Limpiar</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Buscar (nombre/teléfono)</label>
            <div className="relative mt-1">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]"
                data-testid="filter-q" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Canal</label>
            <select value={filters.channel} onChange={(e) => setFilters({ ...filters, channel: e.target.value })} className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" data-testid="filter-channel">
              <option value="">Todos</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="messenger">Messenger</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Empresa CYH/SA</label>
            <select value={filters.empresa} onChange={(e) => setFilters({ ...filters, empresa: e.target.value })} className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" data-testid="filter-empresa">
              <option value="">Cualquiera</option>
              <option value="CYH">CYH</option>
              <option value="SA">SA</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Departamento</label>
            <select value={filters.departamento} onChange={(e) => setFilters({ ...filters, departamento: e.target.value })} className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]" data-testid="filter-dept">
              <option value="">Todos</option>
              {departamentos.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] overflow-hidden">
        <header className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <h3 className="text-sm font-black text-[var(--text)]">{clients.length} contacto(s)</h3>
          {loading && <CircleNotch size={16} className="animate-spin text-[var(--text-muted)]" />}
        </header>
        {clients.length === 0 ? (
          <div className="p-10 text-center text-[var(--text-muted)] text-sm">{loading ? 'Cargando...' : 'Sin contactos'}</div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-xs" style={{ minWidth: '900px' }}>
              <thead className="bg-[var(--bg)] text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                <tr>
                  <th className="px-3 py-2 text-left">Contacto</th>
                  <th className="px-3 py-2 text-left">Teléfono</th>
                  <th className="px-3 py-2 text-center">Canal</th>
                  <th className="px-3 py-2 text-left">Negocio</th>
                  <th className="px-3 py-2 text-center">CYH/SA</th>
                  <th className="px-3 py-2 text-left">Etiquetas</th>
                  <th className="px-3 py-2 text-center">Primer</th>
                  <th className="px-3 py-2 text-center">Último</th>
                  <th className="px-3 py-2 text-center">Estado lead</th>
                  <th className="px-3 py-2 text-center">Cliente</th>
                  <th className="px-3 py-2 text-center">Editar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {clients.map(c => (
                  <tr key={c.id} className="hover:bg-[var(--bg)]" data-testid={`social-client-${c.id}`}>
                    <td className="px-3 py-2 text-[var(--text)] font-bold">{c.name || '—'}</td>
                    <td className="px-3 py-2 text-[var(--text)] whitespace-nowrap">
                      {c.phone ? (
                        <a href={`https://wa.me/${(c.phone || '').replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-[#1D4ED8] hover:underline">{c.phone}</a>
                      ) : <span className="text-[var(--text-faint)]">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {c.channel === 'whatsapp' && <WhatsappLogo size={14} className="text-emerald-500 inline" />}
                      {c.channel === 'messenger' && <MessengerLogo size={14} className="text-blue-500 inline" />}
                      {!c.channel && <span className="text-[var(--text-faint)]">—</span>}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-muted)] text-[11px]">{c.business_name || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-1">
                        {(c.empresas || []).map(e => (
                          <span key={e} className="text-[9px] font-black px-1.5 py-0.5 rounded bg-[#1D4ED8]/15 text-[#1D4ED8] border border-[#1D4ED8]/30">{e}</span>
                        ))}
                        {(c.empresas || []).length === 0 && <span className="text-[10px] text-[var(--text-faint)]">—</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-[10px] text-[var(--text-muted)]">{(c.tags || []).join(', ') || '—'}</td>
                    <td className="px-3 py-2 text-center text-[10px] text-[var(--text-muted)]">{fmtDate(c.first_seen)}</td>
                    <td className="px-3 py-2 text-center text-[10px] text-[var(--text-muted)]">{fmtDate(c.last_seen)}</td>
                    <td className="px-3 py-2 text-center">
                      <select value={c.lead_estado || 'Nuevo'} data-testid={`lead-estado-${c.id}`}
                        onChange={(e) => cambiarEstado(c, e.target.value)}
                        className={`text-[10px] font-bold border rounded px-1 py-0.5 bg-[var(--surface)] ${c.lead_estado === 'Convertido a cliente' ? 'text-emerald-600 border-emerald-500/40' : c.lead_estado === 'Perdido' ? 'text-red-500 border-red-500/40' : 'text-[var(--text)] border-[var(--border)]'}`}>
                        {ESTADOS_LEAD.map((e) => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {c.cliente_id ? (
                        <span className="text-[10px] font-black text-emerald-600" data-testid={`lead-cliente-${c.id}`}>{c.cliente_id}</span>
                      ) : (
                        <button onClick={() => setConvirtiendo(c)} className="text-[10px] font-bold text-[#1D4ED8] hover:underline whitespace-nowrap" data-testid={`convertir-${c.id}`}>
                          → Cliente
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => setEditing(c)} className="text-[#1D4ED8] hover:underline" data-testid={`edit-${c.id}`}>
                        <NotePencil size={14} weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-[var(--border)]">
              {clients.map(c => (
                <div key={c.id} className="p-3" data-testid={`social-client-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-black text-[var(--text)] text-sm truncate flex items-center gap-1">
                        {c.channel === 'whatsapp' && <WhatsappLogo size={12} className="text-emerald-500" />}
                        {c.channel === 'messenger' && <MessengerLogo size={12} className="text-blue-500" />}
                        {c.name || '—'}
                      </p>
                      {c.phone && (
                        <a href={`https://wa.me/${(c.phone || '').replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-[#1D4ED8] hover:underline text-xs font-bold flex items-center gap-1 mt-1">
                          <Phone size={11} />{c.phone}
                        </a>
                      )}
                    </div>
                    <button onClick={() => setEditing(c)} className="text-[#1D4ED8] shrink-0 p-1" data-testid={`edit-mobile-${c.id}`}>
                      <NotePencil size={16} weight="bold" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
                    {c.business_name && <span className="bg-[var(--surface-muted)] px-1.5 py-0.5 rounded text-[var(--text-muted)] font-bold">{c.business_name}</span>}
                    {c.departamento && <span className="bg-amber-500/15 text-amber-700 px-1.5 py-0.5 rounded font-bold">📍 {c.departamento}</span>}
                    {(c.empresas || []).map(e => (
                      <span key={e} className="font-black px-1.5 py-0.5 rounded bg-[#1D4ED8]/15 text-[#1D4ED8] border border-[#1D4ED8]/30">{e}</span>
                    ))}
                  </div>
                  {(c.tags || []).length > 0 && (
                    <p className="text-[10px] text-[var(--text-muted)] mt-1">Tags: <span className="font-bold text-[var(--text)]">{c.tags.join(', ')}</span></p>
                  )}
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">{fmtDate(c.first_seen)} → {fmtDate(c.last_seen)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <select value={c.lead_estado || 'Nuevo'} data-testid={`lead-estado-m-${c.id}`}
                      onChange={(e) => cambiarEstado(c, e.target.value)}
                      className="text-[10px] font-bold border border-[var(--border)] rounded px-1 py-1 bg-[var(--surface)] text-[var(--text)] flex-1">
                      {ESTADOS_LEAD.map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                    {c.cliente_id ? (
                      <span className="text-[10px] font-black text-emerald-600">{c.cliente_id}</span>
                    ) : (
                      <button onClick={() => setConvirtiendo(c)} className="text-[10px] font-bold text-white bg-[#1D4ED8] rounded px-2 py-1" data-testid={`convertir-m-${c.id}`}>→ Cliente</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {editing && <EditMetaModal client={editing} departamentos={departamentos} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchClients(); }} />}
      {convirtiendo && <ConvertirModal lead={convirtiendo} onClose={() => setConvirtiendo(null)} onDone={() => { setConvirtiendo(null); fetchClients(); }} />}
    </div>
  );
};

const ConvertirModal = ({ lead, onClose, onDone }) => {
  const [sa, setSa] = useState(true);
  const [cyh, setCyh] = useState(false);
  const [dup, setDup] = useState(null);
  const [saving, setSaving] = useState(false);
  const convertir = async (opts = {}) => {
    setSaving(true);
    try {
      const { data } = await api.post('/clientes/convertir-lead', {
        customer_id: lead.id, empresa_sa: sa, empresa_cyh: cyh, ...opts });
      if (data.duplicado) { setDup(data.duplicado); return; }
      toast.success(`Lead vinculado al cliente ${data.cliente.id}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo convertir'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="convertir-modal"
      onClick={onClose} onKeyDown={(e) => e.key === 'Escape' && onClose()} tabIndex={-1}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4 w-full max-w-md space-y-3 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <p className="font-black text-[var(--text)]">Convertir lead a cliente</p>
        <p className="text-xs text-[var(--text-muted)]">
          <b className="text-[var(--text)]">{lead.name || 'Sin nombre'}</b> · {lead.platform || lead.channel || 'canal'} · {lead.phone || lead.email || ''}<br />
          El lead original se conserva en Redes Sociales y quedará vinculado a la ficha maestra.
        </p>
        {dup && (
          <div className="border border-amber-500/50 bg-amber-500/10 rounded-lg p-2.5 space-y-2" data-testid="convertir-duplicado">
            <p className="text-xs font-black text-amber-600">⚠️ Cliente existente encontrado</p>
            <p className="text-xs text-[var(--text)]">{dup.id} — <b>{dup.nombre}</b> · {dup.telefono || dup.correo} · Vendedor: {dup.vendedor_nombre || 'libre'}</p>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => convertir({ cliente_id: dup.id })} disabled={saving}
                className="text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 rounded px-2.5 py-1.5" data-testid="convertir-vincular">
                Vincular a este cliente
              </button>
              <button onClick={() => convertir({ forzar: true })} disabled={saving}
                className="text-xs font-bold border border-[var(--border)] rounded px-2.5 py-1.5 text-[var(--text)]" data-testid="convertir-forzar">
                Vincular igual (mismo cliente)
              </button>
            </div>
          </div>
        )}
        <div className="flex gap-4">
          <label className="text-xs font-bold flex items-center gap-1.5 text-[var(--text)]"><input type="checkbox" checked={sa} onChange={(e) => setSa(e.target.checked)} data-testid="convertir-sa" /> SA</label>
          <label className="text-xs font-bold flex items-center gap-1.5 text-[var(--text)]"><input type="checkbox" checked={cyh} onChange={(e) => setCyh(e.target.checked)} data-testid="convertir-cyh" /> CYH</label>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-xs font-bold border border-[var(--border)] rounded px-3 py-2 text-[var(--text)]" data-testid="convertir-cancelar">Cancelar</button>
          {!dup && (
            <button onClick={() => convertir()} disabled={saving}
              className="text-xs font-bold text-white bg-[#1D4ED8] hover:bg-[#1e40af] rounded px-3 py-2" data-testid="convertir-confirmar">
              {saving ? 'Convirtiendo…' : 'Convertir a cliente'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EditMetaModal = ({ client, departamentos = [], onClose, onSaved }) => {
  const [empresas, setEmpresas] = useState(client.empresas || []);
  const [tagsRaw, setTagsRaw] = useState((client.tags || []).join(', '));
  const [notes, setNotes] = useState(client.notes || '');
  const [departamento, setDepartamento] = useState(client.departamento || '');
  const [saving, setSaving] = useState(false);

  const toggleEmpresa = (e) => setEmpresas(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/clientes/redes-sociales/${encodeURIComponent(client.id)}`, {
        empresas, tags: tagsRaw.split(',').map(t => t.trim()).filter(Boolean), notes, departamento,
      });
      toast.success('Cliente actualizado');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="font-black text-[var(--text)]">Editar contacto</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18} /></button>
        </header>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Contacto</p>
            <p className="font-bold text-[var(--text)]">{client.name}</p>
            <p className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Phone size={10} /> {client.phone || '(sin teléfono)'}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Empresa(s) — CYH / SA</p>
            <div className="flex gap-2 mt-1">
              {['CYH', 'SA'].map(e => {
                const active = empresas.includes(e);
                return (
                  <button
                    key={e}
                    type="button"
                    onClick={() => toggleEmpresa(e)}
                    className={`px-3 py-1.5 rounded-md font-black text-xs border transition-colors ${active ? 'bg-[#1D4ED8]/15 text-[#1D4ED8] border-[#1D4ED8]/40' : 'bg-[var(--bg)] text-[var(--text-muted)] border-[var(--border)] hover:bg-[var(--surface-muted)]'}`}
                    data-testid={`toggle-empresa-${e}`}
                  >
                    {active && <Check size={12} weight="bold" className="inline mr-1" />}{e}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">Auto-detectado por mensajes recientes. Puedes asignarlo manualmente para contactos sin contexto.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)] flex items-center gap-1"><MapPin size={10} />Departamento (sector)</label>
            <select value={departamento} onChange={(e) => setDepartamento(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]"
              data-testid="edit-dept">
              <option value="">(Sin asignar)</option>
              {departamentos.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)] flex items-center gap-1"><Tag size={10} />Etiquetas (separadas por coma)</label>
            <input value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} placeholder="VIP, Frecuente"
              className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]"
              data-testid="edit-tags" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Notas</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              className="w-full mt-1 px-2 py-1.5 text-xs rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] resize-none"
              data-testid="edit-notes" />
          </div>
        </div>
        <div className="flex gap-2 px-5 py-4 border-t border-[var(--border)] bg-[var(--surface)]">
          <button onClick={onClose} className="flex-1 px-3 py-2 rounded-md border border-[var(--border)] text-xs font-bold text-[var(--text)] hover:bg-[var(--bg)]">Cancelar</button>
          <button onClick={save} disabled={saving} className="flex-1 px-3 py-2 rounded-md bg-[#1D4ED8] hover:bg-[#1E40AF] text-white text-xs font-bold disabled:opacity-50" data-testid="save-meta">
            {saving ? <CircleNotch size={14} className="animate-spin inline" /> : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientesRedes;
